import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import Shepherd from 'shepherd.js';
import { disposeTour, isTourInProgress, scheduleTour } from '@/lib/tourLifecycle';
import 'shepherd.js/dist/css/shepherd.css';
import '@/styles/tours.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;


/**
 * Tour step configurations by page/context
 */
const getTourSteps = (context, _navigate) => {
  const commonButtons = (tour, isLast = false) => [
    {
      text: 'Skip Tour',
      action: tour.cancel,
      classes: 'shepherd-button-secondary',
    },
    {
      text: isLast ? 'Finish' : 'Next',
      action: isLast ? tour.complete : tour.next,
      classes: 'shepherd-button-primary',
    },
  ];

  const steps = {
    // Welcome/Overview page tour
    overview: [
      {
        id: 'welcome',
        title: "Welcome to Eva Insights",
        text: `
          <p>Use Insights to review workspace activity, feedback and knowledge. Your role determines the tools you can access.</p>
        `,
        attachTo: { element: '[data-testid="overview-page"]', on: 'bottom' },
        buttons: (tour) => commonButtons(tour),
      },
      {
        id: 'kpi-cards',
        title: "Workspace summary",
        text: `
          <p>Review employees using Eva, questions answered, helpful ratings and Knowledge Base coverage. Open a summary card to explore its data and reporting period.</p>
        `,
        attachTo: { element: '[data-testid="overview-page"] .grid', on: 'bottom' },
        buttons: (tour) => commonButtons(tour),
      },
      {
        id: 'checklist',
        title: "Getting started",
        text: `
          <p>Use the available guidance to explore your workspace. Review the result of each action before moving on.</p>
        `,
        attachTo: { element: '[data-testid="onboarding-checklist"]', on: 'left' },
        buttons: (tour) => commonButtons(tour),
      },
      {
        id: 'sidebar',
        title: "Explore Insights",
        text: `
          <p>Use the sidebar to open the pages available to your role, including Conversations, Knowledge Base and Engagement.</p>
        `,
        attachTo: { element: 'nav', on: 'right' },
        buttons: (tour) => commonButtons(tour, true),
      },
    ],
    
    // Knowledge Base page tour
    kb: [
      {
        id: 'kb-intro',
        title: "Knowledge Base content",
        text: `
          <p>Manage the documents Eva can retrieve when answering questions. A document must be published, successfully processed and accessible to the user before it can support an answer.</p>
        `,
        buttons: (tour) => commonButtons(tour),
      },
      {
        id: 'kb-upload',
        title: "Upload documents",
        text: `
          <p>Upload a file or a batch using the supported formats and limits shown in the upload dialog. New uploads start as drafts, and processing can begin in the background.</p>
        `,
        attachTo: { element: '[data-testid="upload-doc-btn"]', on: 'bottom' },
        buttons: (tour) => commonButtons(tour, true),
      },
    ],
    
    // Topics page tour
    topics: [
      {
        id: 'topics-intro',
        title: "Topics",
        text: `
          <p>Use topics to categorize documents and conversations. Choose names that your team can recognize.</p>
        `,
        buttons: (tour) => commonButtons(tour),
      },
      {
        id: 'topics-create',
        title: "Create a topic",
        text: `
          <p>Choose Add Topic and enter a clear name and description for the subject you want to organize.</p>
        `,
        attachTo: { element: '[data-testid="add-topic-btn"]', on: 'bottom' },
        buttons: (tour) => commonButtons(tour, true),
      },
    ],
    
    // Team Management page tour
    team: [
      {
        id: 'team-intro',
        title: "Content team",
        text: `
          <p>Manage who can use Eva Insights. Review each member’s role and invitation status here.</p>
        `,
        buttons: (tour) => commonButtons(tour),
      },
      {
        id: 'team-invite',
        title: "Invite a team member",
        text: `
          <p>Use Invite Team Member to choose an existing employee or enter a new colleague’s details. Confirm the role before sending the invitation.</p>
        `,
        attachTo: { element: '[data-testid="invite-member-btn"]', on: 'bottom' },
        buttons: (tour) => commonButtons(tour, true),
      },
    ],
    
    // Analytics page tour
    analytics: [
      {
        id: 'analytics-intro',
        title: "Review activity",
        text: `
          <p>Explore recorded usage, conversation trends and answer feedback. Check the date range when comparing measures.</p>
        `,
        buttons: (tour) => commonButtons(tour),
      },
      {
        id: 'analytics-filters',
        title: "Focus your review",
        text: `
          <p>Use the available filters to narrow the data you are reviewing.</p>
        `,
        attachTo: { element: '[data-testid="analytics-filters"], .filter-bar, [role="combobox"]', on: 'bottom' },
        buttons: (tour) => commonButtons(tour, true),
      },
    ],
  };

  return steps[context] || steps.overview;
};

/**
 * OnboardingTour Hook
 * 
 * Manages the Shepherd.js tour based on the current page context.
 * Automatically shows relevant tour steps when user navigates to a new page
 * as part of the onboarding flow.
 */
export function useOnboardingTour() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tourRef = useRef(null);
  const hasShownTourRef = useRef({});
  
  // Initialize tour
  const initTour = useCallback((context) => {
    if (isTourInProgress()) return null;
    
    
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'shepherd-eva-tour',
        cancelIcon: { enabled: true },
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
        popperOptions: {
          modifiers: [{ name: 'offset', options: { offset: [0, 12] } }],
        },
      },
    });
    
    const steps = getTourSteps(context, navigate);
    
    steps.forEach((step, index) => {
      const totalSteps = steps.length;
      const progressHtml = `
        <div class="shepherd-progress">
          ${Array.from({ length: totalSteps }, (_, i) => `
            <div class="shepherd-progress-dot ${i < index ? 'completed' : ''} ${i === index ? 'active' : ''}"></div>
          `).join('')}
        </div>
      `;
      
      tour.addStep({
        id: step.id,
        title: step.title,
        text: progressHtml + step.text,
        attachTo: step.attachTo,
        buttons: typeof step.buttons === 'function' ? step.buttons(tour) : step.buttons,
        when: {
          show: () => {
            // Add highlight class to target element
            const target = document.querySelector(step.attachTo?.element);
            if (target) {
              target.classList.add('shepherd-target-highlight');
            }
          },
          hide: () => {
            // Remove highlight class
            const target = document.querySelector(step.attachTo?.element);
            if (target) {
              target.classList.remove('shepherd-target-highlight');
            }
          },
        },
      });
    });
    
    tour.on('complete', async () => {
      // Mark tour as completed in backend
      try {
        await fetch(`${API_URL}/api/onboarding/complete-step`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ step_key: 'welcome' }),
        });
      } catch (e) {
        console.error('Failed to mark tour complete:', e);
      }
      hasShownTourRef.current[context] = true;
    });
    
    tour.on('cancel', () => {
      hasShownTourRef.current[context] = true;
    });
    
    tourRef.current = tour;
    return tour;
  }, [navigate, token]);
  
  // Start tour for a specific context
  const startTour = useCallback((context = 'overview') => {
    const tour = initTour(context);
    if (!tour) return;
    
    // Small delay to ensure DOM is ready
    scheduleTour(tour, 500);
  }, [initTour]);
  
  // Check if should show tour for current page
  const checkAndShowTour = useCallback(async () => {
    if (!token || !user) return;
    
    // Determine context from current path
    let context = 'overview';
    const path = location.pathname;
    
    if (path.includes('/kb/content') || path.includes('/knowledge-base')) {
      context = 'kb';
    } else if (path.includes('/topics') || path.includes('/kb/topics')) {
      context = 'topics';
    } else if (path.includes('/team') || path.includes('/people/team')) {
      context = 'team';
    } else if (path.includes('/analytics') || path.includes('/activity')) {
      context = 'analytics';
    }
    
    // Don't show if already shown
    if (hasShownTourRef.current[context]) return;
    
    // Check if user should see onboarding
    try {
      const response = await fetch(`${API_URL}/api/onboarding/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Show tour if onboarding is active and welcome step isn't complete
        if (data.show_onboarding && !data.onboarding_skipped) {
          const welcomeStep = data.steps?.find(s => s.key === 'welcome');
          if (!welcomeStep?.completed) {
            // Only auto-start on overview page
            if (context === 'overview') {
              startTour(context);
            }
          }
        }
      }
    } catch (e) {
      console.error('Failed to check onboarding status:', e);
    }
  }, [token, user, location.pathname, startTour]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.off('complete');
        tourRef.current.off('cancel');
        disposeTour(tourRef.current);
        tourRef.current = null;
      }
    };
  }, []);
  
  return {
    startTour,
    checkAndShowTour,
    tour: tourRef.current,
  };
}

/**
 * OnboardingTourProvider Component
 * 
 * Wrap your app with this to enable automatic tour triggering.
 */
export function OnboardingTourProvider({ children }) {
  const { checkAndShowTour } = useOnboardingTour();
  
  useEffect(() => {
    // Check tour status when component mounts
    const timer = setTimeout(checkAndShowTour, 1000);
    return () => clearTimeout(timer);
  }, [checkAndShowTour]);
  
  return children;
}

export default useOnboardingTour;
